import * as React from "react";

interface Review {
  id: string;
  review_text: string;
  reviewer_name: string | null;
  rating: number | null;
  source_url: string | null;
}

interface ReviewsCarouselProps {
  reviews: Review[];
}

const ReviewsCarousel = ({ reviews }: ReviewsCarouselProps) => {
  const [paused, setPaused] = React.useState(false);

  // Build one "half" of the track by repeating the reviews enough times to
  // cover wide viewports; the track renders half twice so a -50% translate
  // loops seamlessly.
  const half = React.useMemo(() => {
    const repeats = Math.max(1, Math.ceil(4 / Math.max(reviews.length, 1)));
    return Array.from({ length: repeats }).flatMap(() => reviews);
  }, [reviews]);

  const track = React.useMemo(() => [...half, ...half], [half]);
  const duration = Math.max(half.length * 14, 40); // seconds for a full loop

  const handleBlurCapture = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
      setPaused(false);
    }
  };

  return (
    <div
      className="reviews-marquee w-full relative"
      data-paused={paused || undefined}
      role="region"
      aria-label="Game reviews. Scrolls automatically and pauses while hovered or focused."
      tabIndex={0}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={handleBlurCapture}
    >
      <div
        className="reviews-marquee-track flex w-max"
        style={{ "--marquee-duration": `${duration}s` } as React.CSSProperties}
      >
        {track.map((review, index) => (
          <div key={`${review.id}-${index}`} className="w-[320px] sm:w-[380px] lg:w-[440px] shrink-0 pr-6">
            <article className="overflow-hidden group border border-border bg-[#0c0c0c] h-full relative p-8 md:p-12">
              <div className="absolute top-0 left-0 w-full h-[2px] bg-blood-red opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              {/* Quote circle icon */}
              <div className="mx-auto w-16 rounded-full flex items-center justify-center">
                <span className="text-blood-red font-serif relative top-[15px]" style={{ fontSize: "70px" }}>
                  "
                </span>
              </div>

              {/* Review text */}
              <p className="text-muted-foreground text-center leading-relaxed mt-4">{review.review_text}</p>

              {/* Reviewer name */}
              {review.reviewer_name && (
                <p className="text-muted-foreground text-center text-sm font-semibold mt-4">— {review.reviewer_name}</p>
              )}
            </article>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReviewsCarousel;
